import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogOut } from "lucide-react";
import LinksManager from "@/components/LinksManager";
import EternalLinksManager from "@/components/EternalLinksManager";
import StatsPanel from "@/components/StatsPanel";
import { t } from "@/i18n";
import { adminLogin, setAdminPassword, clearAdminPassword, getAdminPassword } from "@/api";
import { toast } from "sonner";

const AdminPanel = ({ open, onOpenChange, lang, onChanged }) => {
  const [isAuthed, setIsAuthed] = useState(!!getAdminPassword());
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("links");

  const handleLogin = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      await adminLogin(pwd);
      setAdminPassword(pwd);
      setIsAuthed(true);
      setPwd("");
      toast.success(t(lang, "loginSuccess"));
    } catch (err) {
      toast.error(t(lang, "loginError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearAdminPassword();
    setIsAuthed(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-dialog"
        className="bg-[#070707] border-[#FF1493]/30 text-white max-w-5xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-white">
            {t(lang, "adminTitle")}
          </DialogTitle>
          <DialogDescription className="font-mono-display text-xs uppercase tracking-widest text-zinc-500">
            Вареник 18+ · control room
          </DialogDescription>
        </DialogHeader>

        {!isAuthed ? (
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-pwd" className="font-mono-display text-xs uppercase tracking-widest text-zinc-400">
                {t(lang, "password")}
              </Label>
              <Input
                id="admin-pwd"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                data-testid="admin-password-input"
                autoFocus
                className="bg-black border-white/15 text-white focus:border-[#FF1493] focus:ring-[#FF1493]/40"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full bg-[#FF1493] hover:bg-[#ff3aa3] text-black font-bold uppercase tracking-widest"
            >
              {t(lang, "enter")}
            </Button>
          </form>
        ) : (
          <div className="space-y-6">
            <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="admin-tabs">
              <TabsList className="grid w-full grid-cols-3 bg-black/60 border border-white/10">
                <TabsTrigger value="links" data-testid="admin-tab-links" className="data-[state=active]:bg-[#FF1493] data-[state=active]:text-black">
                  {t(lang, "tabLinks")}
                </TabsTrigger>
                <TabsTrigger value="eternal" data-testid="admin-tab-eternal" className="data-[state=active]:bg-[#FF1493] data-[state=active]:text-black">
                  {t(lang, "tabEternal")}
                </TabsTrigger>
                <TabsTrigger value="stats" data-testid="admin-tab-stats" className="data-[state=active]:bg-[#FF1493] data-[state=active]:text-black">
                  {t(lang, "tabStats")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="links" className="mt-4">
                <LinksManager lang={lang} onChanged={onChanged} />
              </TabsContent>
              <TabsContent value="eternal" className="mt-4">
                <EternalLinksManager lang={lang} />
              </TabsContent>
              <TabsContent value="stats" className="mt-4">
                <StatsPanel lang={lang} />
              </TabsContent>
            </Tabs>

            <div className="flex justify-end pt-2 border-t border-white/10">
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="admin-logout"
                className="text-zinc-400 hover:text-white hover:bg-white/5"
              >
                <LogOut className="w-4 h-4 mr-2" /> {t(lang, "logout")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminPanel;
